import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import PickupService from "../../../../app/data/pickup/pickupService";
import { PickupsResponseContentModel } from "../../../../app/data/pickup/models";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "./pickupInboxQueryOptions";

export interface DuplicatePickupCriteria {
  bolNumber: string;
  teamId: string;
  shipperZipCode: string;
  consigneeZipCode: string;
}

export interface DuplicatePickupDisplay {
  pickupNumber: string;
  status: string;
  createdAt: string;
}

export const normalizeZip = (zip?: string | null): string => {
  if (!zip) return "";
  const digits = zip.trim().replace(/\D/g, "");
  return digits.slice(0, 5);
};

export const normalizeBol = (bol?: string | null): string =>
  (bol ?? "").trim().toLowerCase();

export const isDuplicatePickup = (
  pickup: PickupsResponseContentModel,
  criteria: DuplicatePickupCriteria
): boolean => {
  const request = pickup.pickupFullData?.pickupRequest;
  if (!request) return false;

  const pickupTeamId = pickup.requester?.teamId;
  if (!pickupTeamId || pickupTeamId !== criteria.teamId) return false;

  if (normalizeBol(request.bolNumber) !== normalizeBol(criteria.bolNumber)) return false;

  if (normalizeZip(request.shipperZip) !== normalizeZip(criteria.shipperZipCode)) return false;

  if (normalizeZip(request.consigneePostalCode) !== normalizeZip(criteria.consigneeZipCode)) {
    return false;
  }

  return true;
};

export const toDuplicatePickupDisplay = (
  pickup: PickupsResponseContentModel
): DuplicatePickupDisplay => {
  const request = pickup.pickupFullData.pickupRequest;
  const status = pickup.pickup?.pickupStatus ?? pickup.status ?? "";
  const createdAt = request.dateCreated ?? "";

  return {
    pickupNumber: pickup.pickup.pickupNumber,
    status: String(status),
    createdAt,
  };
};

export const searchDuplicatePickups = async (
  criteria: DuplicatePickupCriteria,
  excludePickupNumber?: string
): Promise<DuplicatePickupDisplay[]> => {
  const response = await PickupService.getInstance().getPickups(
    null,
    {
      statuses: [],
      expiredOnly: false,
      showFuture: true,
      search: criteria.bolNumber.trim(),
    },
    { pageSize: 50 }
  );

  if (!response.ok()) {
    throw new Error("Unable to check for duplicate pickups.");
  }

  const content = response.data?.content ?? [];
  const normalizedExclude = excludePickupNumber?.trim().toUpperCase();

  return content
    .filter((pickup) => isDuplicatePickup(pickup, criteria))
    .filter((pickup) => {
      if (!normalizedExclude) return true;
      return pickup.pickup.pickupNumber.trim().toUpperCase() !== normalizedExclude;
    })
    .map(toDuplicatePickupDisplay);
};

export const hasDuplicateCheckCriteria = (criteria: Partial<DuplicatePickupCriteria>): boolean =>
  Boolean(
    criteria.bolNumber?.trim() &&
      criteria.teamId?.trim() &&
      criteria.shipperZipCode?.trim() &&
      criteria.consigneeZipCode?.trim()
  );

export const useDuplicatePickupCheck = (
  criteria: Partial<DuplicatePickupCriteria>,
  excludePickupNumber?: string
) => {
  const enabled = hasDuplicateCheckCriteria(criteria);

  return useQuery({
    queryKey: [
      QUERY_KEYS.PICKUP_DUPLICATE_CHECK,
      criteria.bolNumber,
      criteria.teamId,
      criteria.shipperZipCode,
      criteria.consigneeZipCode,
      excludePickupNumber,
    ],
    queryFn: () =>
      searchDuplicatePickups(criteria as DuplicatePickupCriteria, excludePickupNumber),
    enabled,
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });
};
